import React, { useMemo } from 'react';
import { Descriptions } from 'antd';
import type { DescriptionsProps } from 'antd';

type BalancedDescriptionsProps = DescriptionsProps & {
  children: React.ReactNode;
};

type DescriptionItemElement = React.ReactElement<{ span?: number }>;

const resolveColumnCount = (column: DescriptionsProps['column']): number => {
  if (typeof column === 'number' && Number.isFinite(column)) {
    return Math.max(1, column);
  }
  if (column && typeof column === 'object') {
    const order = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'] as const;
    for (const key of order) {
      const value = column[key];
      if (typeof value === 'number' && value > 0) {
        return value;
      }
    }
  }
  return 3;
};

const clampSpan = (span: number | undefined, columns: number) => {
  if (!span || span <= 0) {
    return 1;
  }
  return Math.min(span, columns);
};

const BalancedDescriptions: React.FC<BalancedDescriptionsProps> = ({
  column = 3,
  children,
  ...rest
}) => {
  const columnCount = useMemo(() => resolveColumnCount(column), [column]);

  const normalizedChildren = useMemo(() => {
    const nodes = React.Children.toArray(children);
    if (nodes.length === 0) {
      return nodes;
    }

    const adjusted = [...nodes];
    let currentRowSpan = 0;

    const stretchPrevious = (index: number, remaining: number) => {
      if (remaining <= 0 || index < 0 || index >= adjusted.length) {
        return;
      }
      const previousNode = adjusted[index] as DescriptionItemElement;
      if (!React.isValidElement(previousNode)) {
        return;
      }
      const baseSpan = clampSpan(previousNode.props.span, columnCount);
      adjusted[index] = React.cloneElement(previousNode, {
        span: Math.min(baseSpan + remaining, columnCount),
      });
    };

    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i] as DescriptionItemElement;
      if (!React.isValidElement(node)) {
        continue;
      }

      const span = clampSpan(node.props.span, columnCount);

      if (span === columnCount) {
        if (currentRowSpan > 0 && currentRowSpan < columnCount) {
          stretchPrevious(i - 1, columnCount - currentRowSpan);
        }
        currentRowSpan = 0;
        continue;
      }

      if (currentRowSpan + span > columnCount) {
        if (currentRowSpan > 0 && currentRowSpan < columnCount) {
          stretchPrevious(i - 1, columnCount - currentRowSpan);
        }
        currentRowSpan = span;
      } else if (currentRowSpan + span === columnCount) {
        currentRowSpan = 0;
      } else {
        currentRowSpan += span;
      }
    }

    if (currentRowSpan > 0 && currentRowSpan < columnCount) {
      stretchPrevious(adjusted.length - 1, columnCount - currentRowSpan);
    }

    return adjusted;
  }, [children, columnCount]);

  return (
    <Descriptions column={column} {...rest}>
      {normalizedChildren}
    </Descriptions>
  );
};

export default BalancedDescriptions;
